import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ThumbsUp, ThumbsDown, Award, CalendarDays, Loader2 } from "lucide-react";
import { api, errMsg, fileUrl } from "@/lib/api";
import { useFetch } from "@/lib/hooks";
import { DAYS, fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { UAvatar, Verified, Img, Empty, FeaturedBadge } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { JobFields, jobPayload, useJobForm } from "@/pages/user/JobForm";

function BookingDialog({ worker, open, onOpenChange }) {
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useJobForm(user);
  const [when, setWhen] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (!when) throw new Error("Pilih jadwal pengerjaan");
      const { data } = await api.post("/jobs/direct", { ...jobPayload(form), worker_id: worker.id, schedule_at: new Date(when).toISOString() });
      toast.success("Booking terkirim! Koin ditahan di escrow.");
      refreshUser();
      nav(`/jobs/${data.id}`);
    } catch (err) {
      toast.error(err.response ? errMsg(err) : err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto rounded-3xl">
        <DialogHeader><DialogTitle className="font-display">Booking {worker.name}</DialogTitle></DialogHeader>
        <form data-testid="direct-booking-form" onSubmit={submit} className="space-y-4">
          <label className="block"><span className="text-xs font-semibold text-slate-600">Jadwal Pengerjaan</span>
            <input data-testid="booking-schedule-input" type="datetime-local" className="field mt-1" value={when} onChange={(e) => setWhen(e.target.value)} required /></label>
          <JobFields form={form} setForm={setForm} categories={worker.skills} />
          <p className="text-xs text-blue-800 bg-blue-50 rounded-xl px-3 py-2">Saldo Anda: {user.coins} koin. Koin akan ditahan (escrow) saat booking.</p>
          <button data-testid="booking-submit-btn" disabled={busy} className="w-full h-12 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Bayar & Booking
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function WorkerShowcase({ w }) {
  return (
    <>
      <section>
        <h3 className="font-display font-bold mb-2 flex items-center gap-2"><Award className="w-4 h-4 text-amber-500" />Sertifikat</h3>
        {w.certificates.length === 0 ? <p className="text-sm text-slate-500">Belum ada sertifikat.</p> : (
          <div className="flex gap-3 overflow-x-auto no-scrollbar">
            {w.certificates.map((c) => (
              <a key={c.id} href={fileUrl(c.path)} target="_blank" rel="noreferrer" className="shrink-0 w-32">
                <Img path={c.path} className="w-32 h-24 rounded-xl" /><p className="text-xs font-semibold mt-1 truncate">{c.name}</p>
              </a>
            ))}
          </div>
        )}
      </section>
      <section data-testid="worker-portfolio">
        <h3 className="font-display font-bold mb-2">Portofolio ({w.portfolio.length})</h3>
        {w.portfolio.length === 0 ? <Empty title="Belum ada portofolio" /> : (
          <div className="grid grid-cols-2 gap-3">
            {w.portfolio.map((p) => (
              <div key={p.id} className="bg-white rounded-2xl border overflow-hidden">
                <div className="grid grid-cols-2"><Img path={p.before_photo} className="h-20 w-full" /><Img path={p.after_photo} className="h-20 w-full" /></div>
                <div className="p-2"><p className="text-xs font-bold truncate">{p.title}</p><p className="text-[11px] text-slate-500">{p.category}</p></div>
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
}

export default function WorkerPublic() {
  const { user } = useAuth();
  const { id } = useParams();
  const nav = useNavigate();
  const { data: w } = useFetch(`/workers/${id}`);
  const [open, setOpen] = useState(false);
  if (!w) return <div className="h-64 rounded-2xl bg-slate-200 animate-pulse" />;
  return (
    <div data-testid="worker-public-page" className="space-y-5 fade-up -mx-4 -mt-4">
      <div className="relative">
        <Img path={w.banner} className="w-full h-36 bg-gradient-to-r from-emerald-500 to-teal-700" />
        <button data-testid="back-btn" onClick={() => nav(-1)} className="absolute top-3 left-3 w-9 h-9 rounded-full bg-white/90 grid place-items-center"><ArrowLeft className="w-4 h-4" /></button>
        <div className="absolute -bottom-10 left-4 p-1 bg-white rounded-full"><UAvatar path={w.avatar} name={w.name} className="w-20 h-20" /></div>
      </div>
      <div className="px-4 pt-8 space-y-5">
        <div>
          <div className="flex items-center gap-1.5"><h1 className="font-display text-2xl font-extrabold">{w.name}</h1><Verified show={w.verified} className="w-5 h-5" /></div>
          {w.featured && <div className="mt-1.5"><FeaturedBadge show /></div>}
          <p className="text-sm text-slate-500">{w.regency || "-"} {w.distance_km < 9999 && `· ${w.distance_km} km`} · <span className={w.online ? "text-emerald-600 font-bold" : ""}>{w.online ? "Online" : "Offline"}</span></p>
          <div className="flex gap-4 mt-2 text-sm"><span className="flex items-center gap-1"><ThumbsUp className="w-4 h-4 text-emerald-600" />{w.thumbs_up}</span><span className="flex items-center gap-1"><ThumbsDown className="w-4 h-4 text-red-500" />{w.thumbs_down}</span></div>
          {w.bio && <p className="text-sm text-slate-600 mt-3">{w.bio}</p>}
          <div className="flex flex-wrap gap-2 mt-3">{w.skills.map((s) => <span key={s} className="px-3 py-1 rounded-full bg-emerald-50 text-emerald-800 text-xs font-bold">{s}</span>)}</div>
        </div>
        {user.kyc_status === "approved" ? (
          <button data-testid="direct-book-btn" onClick={() => setOpen(true)} className="w-full h-12 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold transition-colors">Booking Langsung</button>
        ) : (
          <div data-testid="kyc-required-notice" className="rounded-2xl bg-amber-50 border border-amber-200 p-4 space-y-3">
            <p className="text-sm text-amber-800"><b>Verifikasi identitas diperlukan.</b> {user.kyc_status === "pending" ? "Pengajuan KYC Anda sedang diperiksa. Booking langsung bisa dilakukan setelah disetujui." : "Untuk keamanan, booking langsung hanya untuk pengguna yang sudah verifikasi identitas."}</p>
            {user.kyc_status !== "pending" && <button data-testid="go-kyc-btn" onClick={() => nav("/profile/kyc")} className="w-full h-11 rounded-full bg-amber-500 hover:bg-amber-600 text-white font-bold transition-colors">Verifikasi Identitas Sekarang</button>}
          </div>
        )}
        <section>
          <h3 className="font-display font-bold mb-2 flex items-center gap-2"><CalendarDays className="w-4 h-4 text-emerald-600" />Jadwal Ketersediaan</h3>
          <div className="grid grid-cols-7 gap-1 text-center">
            {DAYS.map(([k, l]) => { const d = w.schedule[k] || {}; return (
              <div key={k} className={`rounded-xl py-2 text-[10px] font-bold ${d.active ? "bg-emerald-50 text-emerald-800" : "bg-slate-100 text-slate-400"}`}>
                {l.slice(0, 3)}<br /><span className="font-mono font-medium">{d.active ? `${d.start}` : "Libur"}</span>{d.active && <><br /><span className="font-mono font-medium">{d.end}</span></>}
              </div>); })}
          </div>
        </section>
        <WorkerShowcase w={w} />
        <section>
          <h3 className="font-display font-bold mb-2">Ulasan</h3>
          {w.reviews.length === 0 ? <p className="text-sm text-slate-500">Belum ada ulasan.</p> : w.reviews.map((r) => (
            <div key={r.id} className="bg-white rounded-2xl border p-3 mb-2">
              <p className="text-sm font-bold flex items-center gap-2">{r.thumbs === "up" ? <ThumbsUp className="w-4 h-4 text-emerald-600" /> : <ThumbsDown className="w-4 h-4 text-red-500" />}{r.user_name}</p>
              {r.text && <p className="text-sm text-slate-600 mt-1">{r.text}</p>}
              <p className="text-[11px] text-slate-400 mt-1">{r.job_title} · {fmtDate(r.created_at)}</p>
            </div>
          ))}
        </section>
      </div>
      <BookingDialog worker={w} open={open} onOpenChange={setOpen} />
    </div>
  );
}
